'use client'

import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { useChromeT } from '../../app/i18n/useChromeT'
import { FaCrown } from 'react-icons/fa'
import { usePathname } from 'next/navigation'
import { openConsentSettings } from '../../lib/consent'
import { getPathLocale, localizedPath } from '../../app/i18n/supported-locales'

const footerLinkClass = 'mr-2 underline inline-flex min-h-11 items-center'

// FooterLinks component definition
const Footer = () => {
  const { i18n } = useTranslation()
  const t = useChromeT()
  const pathname = usePathname()
  const homeHref = localizedPath('/', getPathLocale(pathname) || i18n.language)

  const hiddenPaths = ['/chat']

  if (hiddenPaths.includes(pathname)) {
    return null
  }

  return (
    <div className="my-4 px-4">
      <div className="flex-grow border-t border-gray-800 my-6"></div>

      <div className="py-5 grid grid-cols-1 md:flex md:flex-wrap md:space-x-2 justify-center space-y-4 md:space-y-0">
        <div className="text-gray-300 text-sm flex flex-col items-start">
          <p className="text-white font-semibold">{t('footerServices')}</p>
          <div className=" grid grid-cols-2">
            <a href={homeHref} className={footerLinkClass}>
              {t('aiGirlfriend')}
            </a>
          </div>
        </div>
        <div className="text-gray-300 text-sm flex flex-col items-start">
          <p className="text-white font-semibold">{t('footerSocials')}</p>
          <a
            href="https://discord.gg/cZuJFFb34e"
            target="_blank"
            rel="noopener noreferrer"
            className={footerLinkClass}
          >
            Discord
          </a>
          <a
            href="https://www.instagram.com/promptchan.ai"
            target="_blank"
            rel="noopener noreferrer"
            className={footerLinkClass}
          >
            Instagram
          </a>
          <a
            href="https://twitter.com/Promptchan"
            target="_blank"
            rel="noopener noreferrer"
            className={footerLinkClass}
          >
            X (Twitter)
          </a>
        </div>

        <div className="text-gray-300 text-sm flex flex-col items-start">
          <p className="text-white font-semibold">{t('footerBusinessApi')}</p>

          <a
            href="/articles/ai-porn-affiliate-program"
            className={footerLinkClass}
          >
            {t('affiliateGuide')}
          </a>
          <a
            href="/articles/promptchan-api-getting-started"
            className={footerLinkClass}
          >
            {t('apiGuide')}
          </a>
          <a href="/docs" className={footerLinkClass}>
            {t('apiDocs')}
          </a>
          <a
            href={`mailto:admin@promptchan.com?subject=${`Business Inquiry`}&body=${`\n\n\n\n\n\nBusiness Inqiry for Promptchan - Please enter your inquiry above, and the team will get back to you as soon as possible.`}`}
            className={footerLinkClass}
          >
            {t('businessContact')}
          </a>
        </div>

        <div className="text-gray-300 text-sm flex flex-col items-start">
          <p className="text-white font-semibold">{t('footerAbout')}</p>

          <a href={`/articles`} className={footerLinkClass}>
            {t('guide')}
          </a>
          <a href={`/contact`} className={footerLinkClass}>
            {t('contact')}
          </a>
        </div>

        <div className="text-gray-300 text-sm flex flex-col items-start">
          <p className="text-white font-semibold">{t('footerAiTools')}</p>
          <a href={`/explore`} className={footerLinkClass}>
            {t('exploreAiImages')}
          </a>
          <a href={`/generate`} className={footerLinkClass}>
            {t('createAiImages')}
          </a>
          <a href={`/chat`} className={footerLinkClass}>
            {t('aiChat')}
          </a>
        </div>

        <div className="text-gray-300 text-sm flex flex-col items-start">
          <p className="text-white font-semibold">{t('footerShop')}</p>
          <a href={`/upgrade`} className={footerLinkClass}>
            {t('upgradeToPro')}
          </a>
          <a href={`/gems`} className={footerLinkClass}>
            {t('buyGems')}
          </a>
        </div>
      </div>

      <div className="mt-5 flex justify-center items-center space-x-2 text-sm">
        {/* <p className="text-gray-400">
          Copyright © 2024 Promptchan. All rights reserved.
        </p> */}
        <p className="text-white flex flex-wrap text-gray-300">
          <a
            href="https://promptchan.com/termsofservice"
            className={footerLinkClass}
          >
            {t('termsOfService')}
          </a>
          <Link
            href="/privacypolicy"
            prefetch={false}
            className={footerLinkClass}
          >
            {t('privacyPolicy')}
          </Link>
          <Link
            href="/contentremoval"
            prefetch={false}
            className={footerLinkClass}
          >
            {t('contentremoval')}
          </Link>
          <Link
            href={`/dmcapolicy`}
            prefetch={false}
            className={footerLinkClass}
          >
            {t('dmcapolicy')}
          </Link>
          <Link href={`/refund`} prefetch={false} className={footerLinkClass}>
            {t('refundpolicy')}
          </Link>
          <Link
            href={`/contentmoderationpolicy`}
            prefetch={false}
            className={footerLinkClass}
          >
            {t('contentmoderationpolicy')}
          </Link>
          <button
            type="button"
            onClick={openConsentSettings}
            className={footerLinkClass}
          >
            {t('cookieConsent.settings', 'Cookie settings')}
          </button>
          {/* Points at /contact, not a mailto: the FAQ there answers most of what used to
              arrive at this address. The address stays as the label so it can still be copied. */}
          <Link href={`/contact`} prefetch={false} className={footerLinkClass}>
            support@promptchan.com
          </Link>

          <a
            href="https://aiffiliate.io/"
            className={`${footerLinkClass} text-yellow-300 justify-center`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('affiliate')} <FaCrown className="ml-1" />
          </a>
        </p>
      </div>
    </div>
  )
}

export default Footer
